import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { createApiFetch } from './services/apiClient';
import { CONFIG_BACKEND } from './config-backend';
import {
  buildAppContextValue,
  createActionDeps,
  createActionStateSnapshot,
  createAppActions,
  createNavigate,
  createToast,
  getInitialAuthToken,
  getInitialCurrentUser,
  getInitialEmptyList,
  getInitialParams,
  getInitialRoute,
  getInitialTemplates,
  useAuthTokenSync,
  usePersistedNavigationState,
} from './app-state';
const AppCtx = createContext(null);
const useApp = () => useContext(AppCtx);

const API_URL = CONFIG_BACKEND.baseUrl;
const DEBUG_OFF = 'N';
const DEBUG_BACKEND_ENDPOINTS = Object.freeze([
  'POST /api/auth/login',
  'POST /api/auth/logout',
  'GET /api/auth/me',
  'GET /api/users',
  'PATCH /api/users/:userId',
  'DELETE /api/users/:userId',
  'GET /api/lots',
  'GET /api/lots/:lotId',
  'PATCH /api/lots/:lotId',
  'DELETE /api/lots/:lotId',
  'GET /api/approvals',
  'PATCH /api/approvals/:approvalId',
  'GET /api/companies',
  'POST /api/companies',
  'PATCH /api/companies/:companyId',
  'DELETE /api/companies/:companyId',
  'POST /api/companies/:companyId/test-sienge-connection',
  'POST /api/companies/test-sienge-credentials',
  'GET /api/companies/sienge-live-status',
  'GET /api/companies/:companyId/creditors',
  'GET /api/companies/:companyId/creditors/kpis',
  'GET /api/companies/:companyId/creditors/:syncedCreditorRowId/bank-informations',
  'GET /api/companies/:companyId/creditors/:syncedCreditorRowId/address-detail',
  'GET /api/companies/:companyId/creditors/:syncedCreditorRowId/contact-detail',
  'GET /api/companies/:companyId/creditors/:syncedCreditorRowId/payments',
  'POST /api/companies/:companyId/creditors/sync-total',
  'GET /api/companies/:companyId/creditors/sync-total/status',
  'GET /api/companies/:companyId/sync-logs',
  'GET /api/system-settings/sienge-creditor-sync',
  'PATCH /api/system-settings/sienge-creditor-sync',
  'DELETE /api/system-settings/sienge-creditor-sync',
  'GET /api/system-settings/sienge-fields-options',
  'PATCH /api/system-settings/sienge-fields-options',
  'DELETE /api/system-settings/sienge-fields-options',
  'GET /api/system-settings/debug',
  'PATCH /api/system-settings/debug',
  'GET /api/system-settings/dev-reset-preview',
  'DELETE /api/system-settings/dev-reset',
]);
const DEBUG_API_SIENGE_BY_ROUTE = Object.freeze({
  companies: Object.freeze([
    'POST /api/companies/:companyId/test-sienge-connection',
    'POST /api/companies/test-sienge-credentials',
    'GET /api/companies/sienge-live-status',
  ]),
  'company-creditors': Object.freeze([
    'POST /api/companies/:companyId/creditors/sync-total',
    'GET /api/companies/:companyId/creditors/sync-total/status',
    'GET /api/companies/:companyId/sync-logs',
  ]),
  settings: Object.freeze([
    'GET /api/system-settings/sienge-creditor-sync',
    'PATCH /api/system-settings/sienge-creditor-sync',
    'DELETE /api/system-settings/sienge-creditor-sync',
    'GET /api/system-settings/sienge-fields-options',
    'PATCH /api/system-settings/sienge-fields-options',
    'DELETE /api/system-settings/sienge-fields-options',
  ]),
});
const DEBUG_FRONTEND_FILES_BY_ROUTE = Object.freeze({
  dashboard: Object.freeze([
    'pages/page-dashboard.jsx',
    'styles/pages/page-dashboard.css',
  ]),
  import: Object.freeze([
    'pages/page-newpayment.jsx',
    'styles/pages/page-newpayment.css',
  ]),
  'import-new': Object.freeze([
    'pages/page-newpayment.jsx',
    'styles/pages/page-newpayment.css',
  ]),
  approve: Object.freeze([
    'pages/page-approve.jsx',
    'styles/pages/page-approve.css',
  ]),
  payments: Object.freeze([
    'pages/page-payments.jsx',
    'styles/pages/page-payments.css',
  ]),
  'payment-new': Object.freeze([
    'pages/page-newpayment.jsx',
    'styles/pages/page-newpayment.css',
  ]),
  details: Object.freeze([
    'pages/page-batch-details.jsx',
    'styles/pages/page-batch-details.css',
  ]),
  'batch-details': Object.freeze([
    'pages/page-batch-details.jsx',
    'styles/pages/page-batch-details.css',
  ]),
  'payment-logs': Object.freeze([
    'pages/page-payment-logs.jsx',
    'styles/pages/page-payment-logs.css',
  ]),
  companies: Object.freeze([
    'pages/list-empresas.jsx',
    'styles/pages/list-empresas.css',
  ]),
  'company-creditors': Object.freeze([
    'pages/list-creditores.jsx',
    'styles/pages/list-creditores.css',
  ]),
  'company-sync-logs': Object.freeze([
    'pages/page-sync-logs.jsx',
    'styles/pages/page-sync-logs.css',
  ]),
  'company-new': Object.freeze([
    'pages/form-empresas.jsx',
    'styles/pages/form-empresas.css',
  ]),
  templates: Object.freeze([
    'pages/list-arquetipo.jsx',
    'styles/pages/list-arquetipo.css',
  ]),
  'template-new': Object.freeze([
    'pages/form-arquetipo.jsx',
    'styles/pages/form-arquetipo.css',
  ]),
  users: Object.freeze([
    'pages/list-usuarios.jsx',
    'styles/pages/list-usuarios.css',
  ]),
  profile: Object.freeze([
    'pages/form-usuarios.jsx',
    'styles/pages/form-usuarios.css',
  ]),
  settings: Object.freeze([
    'pages/form-config.jsx',
    'styles/pages/form-config.css',
  ]),
  empty: Object.freeze([
    'pages/page-empty.jsx',
    'styles/pages/page-empty.css',
  ]),
  login: Object.freeze([
    'pages/page-login.jsx',
    'styles/pages/page-login.css',
  ]),
});
const DEBUG_FRONTEND_SHARED_FILES = Object.freeze([
  'app-root.jsx',
  'app-provider.jsx',
  'config-backend.js',
  'routes.js',
  'services/apiClient.js',
  'components/index.js',
  'components/sp-shell.jsx',
  'components/sp-primitives.jsx',
  'components/sp-overlays.jsx',
  'components/data.js',
  'components/icon.jsx',
  'components/avatar.jsx',
  'components/status-chip.jsx',
  'components/wordmark.jsx',
  'app-state/index.js',
  'app-state/build-app-context-value.js',
  'app-state/initial-state.js',
  'app-state/provider-hooks.js',
  'app-state/create-app-actions.js',
  'app-state/create-provider-runtime.js',
  'app-state/navigation-state.js',
  'app-state/session-storage.js',
  'app-state/template-storage.js',
  'app-state/sync-mappers.js',
  'app-state/toast-state.js',
  'app-state/actions/index.js',
  'app-state/actions/auth-actions.js',
  'app-state/actions/batch-actions.js',
  'app-state/actions/company-actions.js',
  'app-state/actions/creditor-actions.js',
  'app-state/actions/settings-actions.js',
  'app-state/actions/sync-actions.js',
  'app-state/actions/template-actions.js',
  'hooks/use-creditor-filters.js',
  'hooks/use-creditor-list-data.js',
  'hooks/use-creditor-poll.js',
  'hooks/use-creditor-detail-modals.js',
  'styles/tokens.css',
  'styles/header.css',
  'styles/aside.css',
  'styles/outros.css',
]);

function AppProvider({ children }) {
  const [route, setRoute] = useState(() => getInitialRoute());
  const [params, setParams] = useState(() => getInitialParams());
  const [activeCompanyId, setActiveCompanyId] = useState(0);
  const [companies, setCompanies] = useState(() => getInitialEmptyList());
  const [templates, setTemplates] = useState(() => getInitialTemplates());
  const [users, setUsers] = useState(() => getInitialEmptyList());
  const [batches, setBatches] = useState(() => getInitialEmptyList());
  const [approvals, setApprovals] = useState(() => getInitialEmptyList());
  const [currentUser, setCurrentUser] = useState(() => getInitialCurrentUser());
  const [authToken, setAuthToken] = useState(() => getInitialAuthToken());
  const [workspaceReady, setWorkspaceReady] = useState(() => !getInitialAuthToken());
  const [isSyncing, setIsSyncing] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [persistentErrors, setPersistentErrors] = useState([]);
  const [companyPickerOpen, setCompanyPickerOpen] = useState(false);
  const [diffModalBatch, setDiffModalBatch] = useState(null);
  const [debugEnabled, setDebugEnabled] = useState(DEBUG_OFF);
  const [pendingCompanySelection, setPendingCompanySelection] = useState(() => !!getInitialAuthToken());

  usePersistedNavigationState(route, params);

  useEffect(() => {
    if (!authToken) setWorkspaceReady(true);
  }, [authToken]);

  useEffect(() => {
    if (!authToken) {
      setPendingCompanySelection(false);
      return;
    }
    setPendingCompanySelection(true);
  }, [authToken]);

  const navigate = useMemo(() => createNavigate(setRoute, setParams), []);

  const activeCompany = useMemo(
    () => {
      const selected = companies.find((c) => c.id === activeCompanyId) || null;
      if (selected?.status === 'active') return selected;
      const firstActive = companies.find((c) => c.status === 'active') || null;
      if (firstActive) return firstActive;
      return { id: 0, name: 'Empresa não selecionada', status: 'inactive' };
    },
    [companies, activeCompanyId],
  );

  const toast = useMemo(() => createToast(setToasts), []);

  const apiFetch = useMemo(
    () => createApiFetch({
      baseUrl: API_URL,
      getToken: () => authToken,
      onCriticalError: (err) => {
        setPersistentErrors((curr) => {
          const exists = curr.some((e) => e.path === err.path && e.message === err.message);
          if (exists) return curr;
          return [err, ...curr].slice(0, 8);
        });
      },
    }),
    [authToken],
  );

  const actionState = useMemo(() => createActionStateSnapshot({
    authToken,
    activeCompanyId,
    batches,
    currentUser,
  }), [authToken, activeCompanyId, batches, currentUser]);

  const actionDeps = useMemo(() => createActionDeps({
    apiFetch,
    state: actionState,
    setRoute,
    setParams,
    setAuthToken,
    setCurrentUser,
    setCompanies,
    setTemplates,
    setUsers,
    setApprovals,
    setBatches,
    setIsSyncing,
    setWorkspaceReady,
    setActiveCompanyId,
    toast,
  }), [apiFetch, actionState, toast]);
  const actions = useMemo(() => createAppActions(actionDeps), [actionDeps]);
  useAuthTokenSync(authToken, route, setRoute, actions.syncFromApi);

  useEffect(() => {
    setDebugEnabled(currentUser?.debug_enabled === 'S' ? 'S' : DEBUG_OFF);
  }, [currentUser?.debug_enabled]);

  useEffect(() => {
    if (!authToken || !workspaceReady || !pendingCompanySelection) return;
    const activeCompanies = companies.filter((c) => String(c.status || '').toLowerCase() === 'active');
    if (activeCompanies.length === 1) {
      setActiveCompanyId(activeCompanies[0].id);
      setCompanyPickerOpen(false);
      setPendingCompanySelection(false);
      return;
    }
    if (activeCompanies.length > 1) {
      setActiveCompanyId(0);
      setCompanyPickerOpen(true);
      setPendingCompanySelection(false);
      return;
    }
    setActiveCompanyId(0);
    setCompanyPickerOpen(false);
    setPendingCompanySelection(false);
  }, [authToken, workspaceReady, pendingCompanySelection, companies]);

  useEffect(() => {
    if (!authToken || !workspaceReady) return;
    const companyId = Number(activeCompanyId || 0);
    if (companyId <= 0) {
      setTemplates([]);
      return;
    }
    actions.syncFromApi(authToken, 'templates');
  }, [authToken, workspaceReady, activeCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(() => buildAppContextValue({
    route,
    params,
    navigate,
    currentUser,
    authToken,
    isSyncing,
    workspaceReady,
    activeCompanyId,
    setActiveCompanyId,
    activeCompany,
    companies,
    templates,
    users,
    approvals,
    batches,
    toast,
    toasts,
    persistentErrors,
    dismissPersistentError: (id) => setPersistentErrors((curr) => curr.filter((e) => e.id !== id)),
    clearPersistentErrors: () => setPersistentErrors([]),
    debugEnabled,
    setDebugEnabled,
    debugBackendBaseUrl: API_URL,
    debugBackendEndpoints: DEBUG_BACKEND_ENDPOINTS,
    debugBackendConfig: CONFIG_BACKEND,
    debugApiSiengeByRoute: DEBUG_API_SIENGE_BY_ROUTE,
    debugFrontendFilesByRoute: DEBUG_FRONTEND_FILES_BY_ROUTE,
    debugFrontendSharedFiles: DEBUG_FRONTEND_SHARED_FILES,
    companyPickerOpen,
    setCompanyPickerOpen,
    diffModalBatch,
    setDiffModalBatch,
    actions,
  }), [
    route,
    params,
    currentUser,
    authToken,
    isSyncing,
    workspaceReady,
    activeCompanyId,
    activeCompany,
    companies,
    templates,
    users,
    approvals,
    batches,
    navigate,
    toast,
    toasts,
    persistentErrors,
    debugEnabled,
    companyPickerOpen,
    diffModalBatch,
    actions,
  ]);

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}

export { AppProvider, useApp };
